// App.js - Version avec intégration i18n
import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { 
  MessageCircle, 
  Share2,
  X,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Lightbulb,
  Info,
  Layout,
  Search,
  Database,
  BookOpen,
  GraduationCap,
  ChevronDown,
  Target,
  Zap,
  Compass,
  Sparkles,
  Newspaper,
  Brain,
  Menu,
  Home
} from 'lucide-react';

// IMPORTS Multilingue 
import './CP-Multilingue/i18n'; // Configuration i18n
import './CP-Multilingue/rtl-support.css'; // CSS RTL
import { useI18n } from './CP-Multilingue/useI18n'; 
import LanguageSwitcher from './CP-Multilingue/LanguageSwitcher'; // Sélecteur de langue
 
// Import des composants existants
import Recommandations from './CP-Recom/Recommendations';
import ObjectifDetail from './CP-Objectif/ObjectifDetail.jsx';
import ObjectifFormulation from './CP-Landing/ObjectifFormulation';

import { ManageCompetence } from './components/ManageCompetence';
import ShareCompetence from './components/ShareCompetence';
import AladinnNavigation from './components/AladinnNavigation';
import Dashboard from './components/Dashboard';

import Cours from './CP-Exploration/Cours';
import TrainingWithGPT from './CP-Exploration/TrainingWithGPT';
import ExplorationSpace from './CP-Exploration/ExplorationSpace';

import ObjectifsList from './CP-ActionSpace/ObjectifsList.js';
import ActionSpace from './CP-ActionSpace/ActionSpace';

import AladinnContextualChat from './CP-Chat/AladinnContextualChat';
import WebSearchSpace from './CP-Search/WebSearchSpace';

import KnowledgeBase from './CP-KnowledgeDB/knowledgeBase';
import ConceptBasedQuiz from './CP-Quiz/ConceptBasedQuiz';
import SourceManager from './CP-Sourcing/SourceManager.js';

import InspirationSpace from './CP-Inspiration/InspirationSpace';
import IntentAnalyzerSpace from './CP-Inspiration/IntentAnalyzerSpace';

import AladinnHomePage from './CP-HomePage/AladinnHomePage';
import AladinnLandingPage from './CP-Landing/AladinnLanding';

import AladinnAcademy from './CP-Academy/AladinnAcademy';
import NewsPage from './CP-News/NewsPage';

import AdminDashboard from './CP-Admin/AdminDashboard';
import AdminAuthProvider, { AdminProtectedRoute } from './CP-Admin/AdminAuthContext';

// IMPORTS COMPOSANTS I18N - Remplacent les anciens
import PublicLandingPage from './CP-Public/PublicLandingPage'; // Version i18n
import PublicNavigation from './CP-Public/PublicNavigation';   // Version i18n  
import AuthModal from './CP-Public/AuthModal';               // Version i18n

import JinaAISearch from './components/JinaAISearch.js';

import NotificationBell from "./CP-Notifications/NotificationBell";
import NotificationPage from "./CP-Notifications/NotificationPage";

// Import du gestionnaire de préférences
import userPreferencesManager from './components/UserPreferencesManager';

import FarmRoadDashboard from './CP-Farming/FarmRoadDashboard.jsx';
import FarmRoadImageAnalysis from './CP-Farming/FarmRoadImageAnalysis.jsx';

const isRealUser = (user) => {
  return user && user.email !== 'service@aladinn.fr';
};

// MobileBottomNavigation avec support i18n
const MobileBottomNavigation = ({ onNavigate, currentSection, currentCompetence, isAuthenticated }) => {
  const { t } = useI18n(); // Ajout du hook i18n
  const [explorationMenuOpen, setExplorationMenuOpen] = useState(false);
  const [inspirationMenuOpen, setInspirationMenuOpen] = useState(false);

  // Fermer les menus quand on clique en dehors
  useEffect(() => {
    function handleClickOutside(event) {
      if (explorationMenuOpen && !event.target.closest('.exploration-menu-mobile')) {
        setExplorationMenuOpen(false);
      }
      if (inspirationMenuOpen && !event.target.closest('.inspiration-menu-mobile')) {
        setInspirationMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [explorationMenuOpen, inspirationMenuOpen]);

  // Navigation différente selon l'état de connexion
  if (!isAuthenticated) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-30 lg:hidden">
        <div className="grid grid-cols-4 h-16">
          {/* Home */}
          <button
            onClick={() => onNavigate('home')}
            className="flex flex-col items-center justify-center py-2 text-purple-600 hover:bg-purple-50 transition-colors"
          >
            <Home className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">{t('navigation.home')}</span>
          </button>

          {/* News */}
          <button
            onClick={() => onNavigate('actualites')}
            className="flex flex-col items-center justify-center py-2 text-blue-600 hover:bg-blue-50 transition-colors"
          >
            <Newspaper className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">{t('navigation.news')}</span>
          </button>

          {/* Learning */}
          <button
            onClick={() => onNavigate('academy')}
            className="flex flex-col items-center justify-center py-2 text-green-600 hover:bg-green-50 transition-colors"
          >
            <GraduationCap className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">{t('navigation.learning')}</span>
          </button>

          {/* Login/Register */}
          <button
            onClick={() => onNavigate('auth')}
            className="flex flex-col items-center justify-center py-2 text-amber-600 hover:bg-amber-50 transition-colors"
          >
            <Sparkles className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">{t('navigation.login')}</span>
          </button>
        </div>
      </div>
    );
  }

  // Navigation pour utilisateurs connectés (traduction des labels)
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-30 lg:hidden">
      <div className="grid grid-cols-5 h-16">
        {/* Home / Parcours */}
        <button
          onClick={() => onNavigate('HomePage')}
          className="flex flex-col items-center justify-center py-2 text-purple-600 hover:bg-purple-50 transition-colors"
        >
          <Home className="w-5 h-5 mb-1" />
          <span className="text-xs font-medium">Home</span>
        </button>

        {/* Mes Objectifs */}
        <button
          onClick={() => onNavigate('competencesList')}
          className="flex flex-col items-center justify-center py-2 text-indigo-600 hover:bg-indigo-50 transition-colors"
        >
          <Target className="w-5 h-5 mb-1" />
          <span className="text-xs font-medium">Objectifs</span>
        </button>

        {/* Transformation */}
        <button
          onClick={() => onNavigate('ambition')}
          className="flex flex-col items-center justify-center py-2 text-indigo-600 hover:bg-indigo-50 transition-colors"
        >
          <Search className="w-5 h-5 mb-1" />
          <span className="text-xs font-medium">Transform</span>
        </button>

        {/* Inspiration avec menu déroulant */}
        <div className="relative inspiration-menu-mobile">
          <button
            onClick={() => {
              onNavigate('inspiration');
              setInspirationMenuOpen(!inspirationMenuOpen);
            }}
            className="w-full flex flex-col items-center justify-center py-2 text-amber-600 hover:bg-amber-50 transition-colors"
          >
            <Lightbulb className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Inspire</span>
          </button>
          
          {inspirationMenuOpen && (
            <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
              <button 
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  onNavigate('intentAnalysis');
                  setInspirationMenuOpen(false);
                }}
              >
                <span className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                  Orientation
                </span>
              </button>
              <button 
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  onNavigate('webSource');
                  setInspirationMenuOpen(false);
                }}
              >
                <span className="flex items-center">
                  <Compass className="w-4 h-4 mr-2 text-blue-500" />
                  Recherche 360
                </span>
              </button>
              <button 
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  onNavigate('webSearch');
                  setInspirationMenuOpen(false);
                }}
              >
                <span className="flex items-center">
                  <Zap className="w-4 h-4 mr-2 text-purple-500" />
                  Transformation
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Exploration avec menu déroulant */}
        <div className="relative exploration-menu-mobile">
          <button
            onClick={() => {
              onNavigate('exploration');
              setExplorationMenuOpen(!explorationMenuOpen);
            }}
            className="w-full flex flex-col items-center justify-center py-2 text-teal-600 hover:bg-teal-50 transition-colors"
          >
            <Layout className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Explore</span>
          </button>
          
          {explorationMenuOpen && (
            <div className="absolute bottom-full right-0 mb-2 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
              <button 
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  onNavigate('dataCollection');
                  setExplorationMenuOpen(false);
                }}
              >
                <span className="flex items-center">
                  <Database className="w-4 h-4 mr-2 text-blue-500" />
                  Base de connaissance
                </span>
              </button>
              <button 
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  onNavigate('semanticSearch');
                  setExplorationMenuOpen(false);
                }}
              >
                <span className="flex items-center">
                  <BookOpen className="w-4 h-4 mr-2 text-purple-500" />
                  Exploration
                </span>
              </button>
              <button 
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  onNavigate('knowledgeTransform');
                  setExplorationMenuOpen(false);
                }}
              >
                <span className="flex items-center">
                  <GraduationCap className="w-4 h-4 mr-2 text-yellow-500" />
                  Apprentissage adaptatif
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chat en position flottante */}
      <button
        onClick={() => onNavigate('chat')}
        className="absolute -top-8 right-4 w-12 h-12 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

function App() {
  // AJOUT DU HOOK I18N
  const { isRTL } = useI18n();
  
  // États existants (inchangés)
  const [user, setUser] = useState(null);
  const [currentCompetence, setCurrentCompetence] = useState(null);
  const [currentSection, setCurrentSection] = useState('competences');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // États pour la responsivité
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [topBarCollapsed, setTopBarCollapsed] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState('16rem');
  const [publicSidebarCollapsed, setPublicSidebarCollapsed] = useState(false);
  
  // États pour la version publique
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [demoObjective, setDemoObjective] = useState(null);
   
  // États pour gérer l'ouverture/fermeture des menus déroulants
  const [explorationMenuOpen, setExplorationMenuOpen] = useState(false);
  const [inspirationMenuOpen, setInspirationMenuOpen] = useState(false);
  
  // État pour le parcours par étapes
  const [currentJourneyStep, setCurrentJourneyStep] = useState('ambition');
  const [journeyProgress, setJourneyProgress] = useState({
    ambition: false,
    strategy: false,
    action: false,
    inspiration: false,
    exploration: false
  });

  const navigate = useNavigate();
  const location = useLocation();
  const [authLoading, setAuthLoading] = useState(true);
  const isAdminPage = location.pathname.startsWith('/admin');

  // TOUTE LA LOGIQUE MÉTIER RESTE IDENTIQUE
  // (Je copie votre logique existante sans modification)

  // Détecter la taille d'écran et ajuster la sidebar
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const newIsMobile = width < 768;
      const newIsTablet = width >= 768 && width < 1024;
      
      setIsMobile(newIsMobile);
      setIsTablet(newIsTablet);
      setTopBarCollapsed(width < 640);
      
      if (newIsMobile || newIsTablet) {
        setSidebarCollapsed(false);
        setSidebarWidth('0px');
        setPublicSidebarCollapsed(true);
      } else {
        setSidebarWidth(sidebarCollapsed ? '4rem' : '16rem');
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [sidebarCollapsed]);

  // Fonction pour gérer le toggle de la sidebar
  const handleSidebarToggle = (collapsed) => {
    setSidebarCollapsed(collapsed);
    if (!isMobile && !isTablet) {
      setSidebarWidth(collapsed ? '4rem' : '16rem');
    }
  };

  // Gestionnaire de clic externe pour fermer les menus
  useEffect(() => {
    function handleClickOutside(event) {
      if (explorationMenuOpen && !event.target.closest('.exploration-menu-container')) {
        setExplorationMenuOpen(false);
      }
      if (inspirationMenuOpen && !event.target.closest('.inspiration-menu-container')) {
        setInspirationMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [explorationMenuOpen, inspirationMenuOpen]);

  // Gestion des notifications
  const handleSnackbarOpen = (severity, message) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // FONCTION D'AUTHENTIFICATION avec demo objective
  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuthModal(false);
    handleSnackbarOpen('success', 'Connexion réussie');
    setCurrentSection('competences');
    
    if (demoObjective) {
      navigate('/objectif-detail', { 
        state: { 
          demoObjective: demoObjective,
          fromPublicDemo: true 
        }
      });
      setDemoObjective(null);
    } else {
      navigate('/HomePage');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setCurrentCompetence(null);
      setCurrentSection('competences');
      setCurrentJourneyStep('ambition');
      setJourneyProgress({
        ambition: false,
        strategy: false,
        action: false,
        inspiration: false,
        exploration: false
      });
      setDemoObjective(null);
      handleSnackbarOpen('success', 'Vous avez été déconnecté avec succès');
      navigate('/');
    } catch (error) {
      handleSnackbarOpen('error', 'Erreur lors de la déconnexion');
    }
  };

  // FONCTION pour gérer la démo objective
  const handleDemoObjectiveCreated = (objectiveText) => {
    setDemoObjective(objectiveText);
    setShowAuthModal(true);
    setAuthMode('register');
  };

  // FONCTION pour afficher le modal d'auth
  const handleShowAuthModal = (mode = 'register') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleCompetenceLoaded = (competence) => {
    console.log('App: Compétence chargée/mise à jour:', competence.title);
    setCurrentCompetence(competence);
    
    if (competence) {
      setJourneyProgress(prev => ({
        ...prev,
        ambition: true,
        strategy: !!competence.recommendation,
        action: !!competence.actionPlan,
        inspiration: !!competence.webSources && competence.webSources.length > 0,
        exploration: !!competence.knowledgeBase
      }));
    }
  };
  
  // Gestion des objectifs avec ManageCompetence
  const {
    currentCompetenceProgress,
    level,
    error,
    loading,
    isEditingTitle,
    generatedMessage,
    customMessage,
    setCustomMessage,
    handleCompetenceUpdate,
    handleProgressUpdate,
    handleDeleteCompetence,
    handleRegenerateCourse,
    handleEditTitle,
    handleSelectCompetence,
  } = ManageCompetence(user, handleSnackbarOpen, currentCompetence, setCurrentCompetence);

  // Fonction améliorée pour sélectionner une compétence
  const handleSelectCompetenceEnhanced = async (competence) => {
    if (!competence) {
      setCurrentCompetence(null);
      // Effacer l'objectif courant dans les préférences si l'utilisateur est connecté
      try {
        if (user?.uid) {
          await userPreferencesManager.clearCurrentObjective(user.uid);
        }
      } catch (e) {
        console.error('App: erreur clearCurrentObjective sur désélection:', e);
      }
      return;
    }
    
    console.log('App: Sélection de la compétence:', competence.title || competence.name || 'Sans titre');
    handleSelectCompetence(competence);
    handleCompetenceLoaded(competence);

    // Persister l'objectif courant côté préférences utilisateur
    try {
      if (user?.uid) {
        await userPreferencesManager.setCurrentObjective(user.uid, {
          id: competence.id,
          title: competence.title || competence.name || '',
          theme: competence.theme || competence.domain || competence.chapitre || null,
          chapitre: competence.chapitre || null
        });
      }
    } catch (e) {
      console.error('App: erreur setCurrentObjective lors de la sélection:', e);
    }
    
    if (competence.actionPlan && competence.actionPlan.steps && competence.actionPlan.steps.length > 0) {
      setCurrentJourneyStep('action');
    } else if (competence.recommendation) {
      setCurrentJourneyStep('strategy');
    } else {
      setCurrentJourneyStep('ambition');
    }
  };

  // Surveillance de l'authentification
  useEffect(() => {
    setAuthLoading(true);
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        // Charger l'objectif courant depuis UserPreferencesManager
        try {
          const currentObjective = await userPreferencesManager.getCurrentObjective(currentUser.uid);
          if (currentObjective && currentObjective.id) {
            console.log('App: Objectif courant chargé depuis préférences:', currentObjective);
            // Note: L'objectif est chargé, mais nous devons récupérer les données complètes
            // via ManageCompetence ou une autre méthode
          }
        } catch (error) {
          console.error('Erreur lors du chargement de l\'objectif courant:', error);
        }
      } else {
        setUser(null);
        setCurrentCompetence(null);
        setCurrentSection('competences');
        setCurrentJourneyStep('welcome');
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);
  
  // Mise à jour de l'objectif depuis la route
  useEffect(() => {
    if (location.state?.competence) {
      handleCompetenceLoaded(location.state.competence);
      setCurrentSection('details');
    }
  }, [location.state]);

  // Gestion des sources et recommandations obtenues dans l'espace inspiration
  const handleInspirationResults = (results) => {
    if (results && results.sources) {
      console.log("Sources d'inspiration obtenues:", results.sources.length);
      handleSnackbarOpen('success', `${results.sources.length} sources d'inspiration trouvées`);
      
      setJourneyProgress(prev => ({ ...prev, inspiration: true }));
      
      navigate(`/webSource/${currentCompetence.id}`, { 
        state: { 
          sources: results.sources,
          competence: currentCompetence,
          userId: user?.uid,
          fromInspiration: true
        }
      });
    }
  };

  // FONCTION de navigation qui gère public/privé
  const handleNavigation = (sectionId, stepData = null) => {
    console.log('App: Navigation vers:', sectionId, stepData);
    setCurrentSection(sectionId);
    
    setExplorationMenuOpen(false);
    setInspirationMenuOpen(false);
    
    // Navigation publique (non connecté)
    if (!user) {
      switch (sectionId) {
        case 'home':
          navigate('/');
          break;
        case 'actualites':
          navigate('/actualites');
          break;
        case 'academy':
          navigate('/academy');
          break;
        case 'auth':
          setShowAuthModal(true);
          setAuthMode('register');
          break;
        default:
          setShowAuthModal(true);
          setAuthMode('register');
          break;
      }
      return;
    }

    // Navigation privée (connecté) - logique existante INCHANGÉE
    switch (sectionId) {
      case 'HomePage':
        setCurrentJourneyStep('ambition');
        if (currentCompetence?.id) {
          navigate('/HomePage');
        } else if (user?.uid) {
          // Utiliser l'objectif courant persistant pour une navigation profonde
          userPreferencesManager.getCurrentObjective(user.uid)
            .then((obj) => {
              if (obj?.id) {
                navigate(`/HomePage/${obj.id}`);
              } else {
                navigate('/HomePage');
              }
            })
            .catch(() => navigate('/HomePage'));
        } else {
          navigate('/HomePage');
        }
        break;
        
      case 'ambition':
        setCurrentJourneyStep('ambition');
        navigate('/Ambition');
        break;
        
      case 'competencesList':
        navigate('/competencesList');
        break;  
        
      case 'details':
      case 'strategy':
        if (currentCompetence) {
          setCurrentJourneyStep('strategy');
          navigate(`/Recommandations/${currentCompetence.id}`, { 
            state: { competence: currentCompetence, userId: user?.uid }
          }); 
        } else { 
          navigate('/competencesList'); 
        }
        break;
        
      case 'academy':
        navigate('/academy');
        break;
        
      case 'actualites':
        navigate('/actualites');
        break;
        
      case 'cours':
        navigate(currentCompetence ? `/cours/${currentCompetence.id}` : '/competencesList', { 
          state: { competence: currentCompetence, userId: user?.uid }
        }); 
        break;
        
      case 'quiz':
        navigate(currentCompetence ? `/quiz/${currentCompetence.id}` : '/competencesList', { 
          state: { competence: currentCompetence, userId: user?.uid }
        }); 
        break;
        
      case 'actionPlan':
      case 'action':
        if (currentCompetence) {
          setCurrentJourneyStep('action');
          navigate(`/actionPlan/${currentCompetence.id}`, { 
            state: { competence: currentCompetence, userId: user?.uid }
          });
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'training':
        navigate(currentCompetence ? `/training/${currentCompetence.id}` : '/competencesList', { 
          state: { competence: currentCompetence, userId: user?.uid }
        }); 
        break;
        
      case 'knowledgeBase':
        navigate(currentCompetence ? `/knowledgeBase/${currentCompetence.id}` : '/competencesList', { 
          state: { competence: currentCompetence, userId: user?.uid }
        }); 
        break;
        
      case 'chat':
        if (currentCompetence?.id) {
          navigate(`/chat/${currentCompetence.id}`);
        } else if (user?.uid) {
          // Fallback: utiliser l'objectif courant depuis les préférences
          userPreferencesManager.getCurrentObjective(user.uid)
            .then((obj) => {
              if (obj?.id) {
                navigate(`/chat/${obj.id}`);
              } else {
                handleSnackbarOpen('warning', "Sélectionnez d'abord un objectif dans 'Mes Objectifs'.");
                navigate('/competencesList');
              }
            })
            .catch((e) => {
              console.error('App: erreur récupération objectif courant pour Chat:', e);
              handleSnackbarOpen('warning', "Sélectionnez d'abord un objectif dans 'Mes Objectifs'.");
              navigate('/competencesList');
            });
        } else {
          handleSnackbarOpen('warning', "Connectez-vous puis sélectionnez un objectif.");
          navigate('/');
        }
        break;
        
      case 'competencesList':
        navigate('/competencesList', { state: { userId: user?.uid } });
        break;
        
      case 'competences-acquises':
        navigate('/competences-acquises', { state: { userId: user?.uid } });
        break;
        
      case 'inspiration':
        if (currentCompetence && currentCompetence.id) {
          setCurrentJourneyStep('inspiration');
          navigate(`/inspiration/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;  
        
      case 'webSource':
        if (currentCompetence && currentCompetence.id) {
          setCurrentJourneyStep('inspiration');
          navigate(`/webSource/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'intentAnalysis':
        if (currentCompetence && currentCompetence.id) {
          navigate(`/intent-analysis/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'exploration':
        if (currentCompetence && currentCompetence.id) {
          setCurrentJourneyStep('exploration');
          navigate(`/exploration/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'dataCollection':
        if (currentCompetence && currentCompetence.id) {
          navigate(`/dataCollection/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'semanticSearch':
        if (currentCompetence && currentCompetence.id) {
          navigate(`/semanticSearch/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'knowledgeTransform':
        if (currentCompetence && currentCompetence.id) {
          navigate(`/knowledgeTransform/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'webSearch':
        if (currentCompetence && currentCompetence.id) {
          navigate(`/webSearch/${currentCompetence.id}`);
        } else {
          navigate('/competencesList');
        }
        break;
        
      case 'dashboard':
        navigate('/dashboard', { state: { userId: user?.uid }});
        break;
        
      case 'partage':
        if (currentCompetence && currentCompetence.id) {
          navigate(`/share/${currentCompetence.id}`);
        } else {
          handleSnackbarOpen('error', 'Veuillez d\'abord sélectionner un objectif');
          navigate('/competencesList');
        }
        break;

      case 'farmroad':
        navigate('/farmroad');
        break;

      case 'farmroad-images':
        navigate('/farmroad/analysis');
        break;
    }
  };

  // Gérer les mises à jour de progression avec notification du parcours
  const handleProgressUpdateEnhanced = (progressData) => {
    console.log('App: Mise à jour de progression:', progressData);
    handleProgressUpdate(progressData);
    
    if (currentCompetence) {
      setJourneyProgress(prev => ({
        ...prev,
        exploration: true
      }));
    }
  };

  // Gérer la création/mise à jour d'un plan d'action
  const handleActionPlanUpdate = () => {
    console.log('App: Plan d\'action mis à jour');
    setJourneyProgress(prev => ({ ...prev, action: true }));
    handleSnackbarOpen('success', 'Plan d\'action mis à jour avec succès');
  };

  const competenceDisplayProps = {
    competence: currentCompetence,
    currentCompetenceProgress,
    level,
    isEditingTitle,
    userId: user?.uid,
    onEditTitle: handleEditTitle,
    onUpdate: handleCompetenceUpdate,
    onDelete: handleDeleteCompetence,
    onRegenerate: handleRegenerateCourse,
    onProgressUpdate: handleProgressUpdateEnhanced,
  };

  // ÉCRANS DE CHARGEMENT ET D'ERREUR (inchangés)
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-3" />
          <span className="text-gray-700 text-sm lg:text-base">Vérification de l'authentification...</span>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-3" />
          <span className="text-gray-700 text-sm lg:text-base">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <AlertTriangle className="h-6 w-6 text-red-500 mx-auto mb-2" />
          <span className="text-red-600 text-sm lg:text-base">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Modal d'authentification - VERSION I18N */}
      {showAuthModal && (
        <AuthModal
          mode={authMode}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          onModeChange={setAuthMode}
          demoObjective={demoObjective}
        />
      )}

      {/* AJOUT: Sélecteur de langue flottant pour toutes les pages */}
      {!isAdminPage && (
        <div className={`fixed top-4 z-50 ${
          isRTL 
            ? 'left-4' 
            : isMobile 
              ? 'right-16'  // Plus d'espace sur mobile
              : 'right-4'   // Position normale sur desktop
        }`}>
          <LanguageSwitcher variant="compact" />
        </div>      
      )}

      {/* Navigation conditionnelle : Public vs Privée */}
      {user && !isAdminPage ? (
        // Navigation pour utilisateurs connectés (existante)
        <AladinnNavigation
          user={user}
          onNavigate={handleNavigation}
          currentSection={currentSection}
          currentCompetence={currentCompetence}
          onLogout={handleLogout}
          onSidebarToggle={handleSidebarToggle}
          sidebarCollapsed={sidebarCollapsed}
        />
      ) : !isAdminPage ? (
        // Navigation publique pour visiteurs - VERSION I18N
        <PublicNavigation
          onNavigate={handleNavigation}
          onShowAuth={handleShowAuthModal}
          currentPath={location.pathname}
          sidebarCollapsed={publicSidebarCollapsed}
          onSidebarToggle={setPublicSidebarCollapsed}
        />
      ) : null}

      {/* Zone principale avec ajustement dynamique selon la sidebar ET RTL */}
      <main 
        className="flex-1 transition-all duration-300 ease-in-out min-w-0"
        style={{
          [isRTL ? 'marginRight' : 'marginLeft']: !isAdminPage && !isMobile && !isTablet ? 
            (user ? sidebarWidth : (publicSidebarCollapsed ? '4rem' : '16rem')) : '0px',
          marginBottom: isMobile && !isAdminPage ? '64px' : '0px'
        }}
      >
        {/* Barre de communication responsive - CONSERVÉE POUR DESKTOP ET TABLET CONNECTÉS */}
        {!isAdminPage && user && (
          <div className="sticky top-0 z-10 bg-white border-b shadow-sm lg:block hidden">
            <div className="max-w-full mx-auto px-2 lg:px-4 py-2 w-full overflow-x-hidden">
              <div className={`flex ${topBarCollapsed ? 'flex-col gap-2' : 'justify-start items-center'} ${isTablet ? 'flex-wrap gap-1' : 'space-x-2 lg:space-x-4'} ${isRTL ? 'flex-row-reverse' : ''}`}>

                <button
                  className={`inline-flex items-center px-2 lg:px-3 py-1.5 text-xs lg:text-sm text-indigo-600 border border-indigo-300 rounded-lg hover:bg-indigo-50 transition-colors ${isTablet ? 'flex-1 justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}
                  onClick={() => handleNavigation('competencesList')}
                >
                  <Target className="w-3 lg:w-4 h-3 lg:h-4 mx-1 lg:mx-1.5 flex-shrink-0" />
                  <span className="truncate">Mes Objectifs</span>
                </button>

                <button
                  className={`inline-flex items-center px-2 lg:px-3 py-1.5 text-xs lg:text-sm text-indigo-600 border border-indigo-300 rounded-lg hover:bg-indigo-50 transition-colors ${isTablet ? 'flex-1 justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}
                  onClick={() => handleNavigation('ambition')}
                >
                  <Search className="w-3 lg:w-4 h-3 lg:h-4 mx-1 lg:mx-1.5 flex-shrink-0" />
                  <span className="truncate">Transformation</span>
                </button>

                {/* Menu déroulant pour Trouver l'inspiration */}
                <div className={`relative inline-block text-left inspiration-menu-container ${isTablet ? 'flex-1' : ''}`}>
                  <button
                    className={`inline-flex items-center px-2 lg:px-3 py-1.5 text-xs lg:text-sm text-amber-600 border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors ${isTablet ? 'w-full justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}
                    onClick={() => { 
                      handleNavigation('inspiration'); 
                      setInspirationMenuOpen(!inspirationMenuOpen);
                    }}                  
                  >
                    <Lightbulb className="w-3 lg:w-4 h-3 lg:h-4 mx-1 lg:mx-1.5 flex-shrink-0" />
                    <span className="truncate">Inspiration</span>
                    <ChevronDown className="w-2 lg:w-3 h-2 lg:h-3 mx-1 flex-shrink-0" />
                  </button>
                  
                  {inspirationMenuOpen && (
                    <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} ${isTablet ? 'dropdown-mobile' : 'mt-2'} w-48 lg:w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-20`}>
                      <div className="py-1">
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            handleNavigation('intentAnalysis');
                            setInspirationMenuOpen(false);
                          }}
                        >
                          <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Sparkles className="w-4 h-4 mx-2 text-amber-500 flex-shrink-0" />
                            <span className="truncate">Orientation</span>
                          </span>
                        </button>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            handleNavigation('webSource');
                            setInspirationMenuOpen(false);
                          }}
                        >
                          <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Compass className="w-4 h-4 mx-2 text-blue-500 flex-shrink-0" />
                            <span className="truncate">Recherche 360</span>
                          </span>
                        </button>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            handleNavigation('webSearch');
                            setInspirationMenuOpen(false);
                          }}
                        >
                          <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Zap className="w-4 h-4 mx-2 text-purple-500 flex-shrink-0" />
                            <span className="truncate">Transformation</span>
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Menu déroulant pour l'espace d'exploration */}
                <div className={`relative inline-block text-left exploration-menu-container ${isTablet ? 'flex-1' : ''}`}>
                  <button
                    className={`inline-flex items-center px-2 lg:px-3 py-1.5 text-xs lg:text-sm text-teal-600 border border-teal-300 rounded-lg hover:bg-teal-50 transition-colors ${isTablet ? 'w-full justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}
                    onClick={() => {
                      handleNavigation('exploration');
                      setExplorationMenuOpen(!explorationMenuOpen); 
                    }}
                  >
                    <Layout className="w-3 lg:w-4 h-3 lg:h-4 mx-1 lg:mx-1.5 flex-shrink-0" />
                    <span className="truncate">Exploration</span>
                    <ChevronDown className="w-2 lg:w-3 h-2 lg:h-3 mx-1 flex-shrink-0" />
                  </button>
                  
                  {explorationMenuOpen && (
                    <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} ${isTablet ? 'dropdown-mobile' : 'mt-2'} w-48 lg:w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-20`}>
                      <div className="py-1">
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            handleNavigation('dataCollection');
                            setExplorationMenuOpen(false);
                          }}
                        >
                          <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Database className="w-4 h-4 mx-2 text-blue-500 flex-shrink-0" />
                            <span className="truncate">Base de connaissance</span>
                          </span>
                        </button>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            handleNavigation('semanticSearch');
                            setExplorationMenuOpen(false);
                          }}
                        >
                          <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <BookOpen className="w-4 h-4 mx-2 text-purple-500 flex-shrink-0" />
                            <span className="truncate">Exploration</span>
                          </span>
                        </button>
                        <button 
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            handleNavigation('knowledgeTransform');
                            setExplorationMenuOpen(false);
                          }}
                        >
                          <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <GraduationCap className="w-4 h-4 mx-2 text-yellow-500 flex-shrink-0" />
                            <span className="truncate">Apprentissage adaptatif</span>
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className={`inline-flex items-center px-2 lg:px-3 py-1.5 text-xs lg:text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors ${isTablet ? 'flex-1 justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}
                  onClick={() => handleNavigation('chat')}
                >
                  <MessageCircle className="w-3 lg:w-4 h-3 lg:h-4 mx-1 lg:mx-1.5 flex-shrink-0" />
                  <span className="truncate">Chat</span>
                </button>
                {/* Icône de notification */}
                {user && <NotificationBell userId={user.uid} />}


              </div>
            </div>
          </div>
        )}

        {/* Navigation mobile en bas - VERSION I18N */}
        {!isAdminPage && (
          <MobileBottomNavigation
            onNavigate={handleNavigation}
            currentSection={currentSection}
            currentCompetence={currentCompetence}
            isAuthenticated={!!user}
          />
        )}

        <div className="max-w-full mx-auto p-3 lg:p-6 w-full overflow-x-hidden">
          <Routes>
            {/* Route principale - VERSION I18N */}
            <Route path="/" element={
              isRealUser(user) ? (
                <Navigate to="/ambition" replace />
              ) : (
                <PublicLandingPage 
                  onShowAuth={handleShowAuthModal}
                  onDemoObjectiveCreated={handleDemoObjectiveCreated}
                />
              )
            } />
            
            {/* Route HomePage avec objectifId optionnel pour liens depuis notifications */}
            <Route path="/HomePage/:objectifId?" element={
              isRealUser(user) ? (
                <AladinnHomePage  
                  userId={user.uid}
                  currentCompetence={currentCompetence}
                  currentJourneyStep={currentJourneyStep}
                  journeyProgress={journeyProgress}
                  onSelectCompetence={handleSelectCompetenceEnhanced}
                  onNavigate={handleNavigation}
                  onStepChange={setCurrentJourneyStep}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/Ambition" element={
              user ? (
                <AladinnLandingPage  
                  userId={user.uid}
                  currentCompetence={currentCompetence}
                  onSelectCompetence={handleSelectCompetenceEnhanced}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/academy/:objectiveId?" element={
              <AladinnAcademy 
                userId={user?.uid || null}
                objectiveId={currentCompetence?.id || null}
                objectiveTitle={currentCompetence?.title || null}
                objectiveTheme={currentCompetence?.domain || currentCompetence?.theme || null}
              />
            } />
            
            <Route path="/actualites/:objectiveId?" element={
              <NewsPage 
                userId={user?.uid || null}
                userEmail={user?.email || null}
                userRole={user ? "user" : "visitor"}
                objectiveId={currentCompetence?.id || null}
                objectiveTitle={currentCompetence?.title || null}
                objectiveTheme={currentCompetence?.domain || null}
              />
            } />

            <Route path="/formulation-objectif" element={
              user ? (
                <ObjectifFormulation />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/objectif-detail" element={
              user ? (
                <ObjectifDetail 
                  userId={user.uid}
                  onCompetenceCreated={async (competence) => {
                    handleCompetenceLoaded(competence);
                    setJourneyProgress(prev => ({ 
                      ...prev, 
                      ambition: true
                    }));
                    setCurrentJourneyStep('strategy');

                    // Persister l'objectif créé comme objectif courant
                    try {
                      await userPreferencesManager.setCurrentObjective(user.uid, {
                        id: competence.id,
                        title: competence.title || competence.name || '',
                        theme: competence.theme || competence.domain || competence.chapitre || null,
                        chapitre: competence.chapitre || null
                      });
                    } catch (e) {
                      console.error('App: erreur setCurrentObjective (création):', e);
                    }
                  }}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/Recommandations/:competenceId" element={
              user ? (
                <Recommandations 
                  {...competenceDisplayProps} 
                  onRecommendationGenerated={() => {
                    setJourneyProgress(prev => ({ ...prev, strategy: true }));
                    setCurrentJourneyStep('action');
                  }}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/webSearch/:competenceId" element={
              user ? (
                <WebSearchSpace competenceId={currentCompetence?.id} />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/webSource/:competenceId" element={
              user ? (
                <SourceManager 
                  propCompetenceId={currentCompetence?.id} 
                  onSourcesSelected={() => {
                    setJourneyProgress(prev => ({ ...prev, inspiration: true }));
                  }}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/inspiration/:competenceId" element={
              user ? (
                <InspirationSpace 
                  propCompetenceId={currentCompetence?.id}
                  onResultsFound={handleInspirationResults}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/exploration/:competenceId/*" element={
              user ? (
                <ExplorationSpace 
                  propCompetenceId={currentCompetence?.id}
                  userId={user?.uid}
                  onResultsFound={handleInspirationResults}
                  onProgressUpdate={handleProgressUpdateEnhanced}
                  onExplorationComplete={() => {
                    setJourneyProgress(prev => ({ ...prev, exploration: true }));
                  }}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/dataCollection/:competenceId" element={
              user ? (
                <Navigate to={`/exploration/${currentCompetence?.id}/dataCollection/${currentCompetence?.id}`} replace />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/semanticSearch/:competenceId" element={
              user ? (
                <Navigate to={`/exploration/${currentCompetence?.id}/semanticSearch/${currentCompetence?.id}`} replace />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/knowledgeTransform/:competenceId" element={
              user ? (
                <Navigate to={`/exploration/${currentCompetence?.id}/knowledgeTransform/${currentCompetence?.id}`} replace />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/cours/:competenceId?" element={
              user ? (
                <Cours 
                  competence={currentCompetence} 
                  userId={user.uid} 
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/quiz/:competenceId?" element={
              user ? (
                <ConceptBasedQuiz 
                  competence={currentCompetence} 
                  onQuizComplete={handleProgressUpdateEnhanced} 
                  userId={user.uid} 
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/training/:competenceId?" element={
              user ? (
                <TrainingWithGPT 
                  competence={currentCompetence} 
                  onExerciseComplete={handleProgressUpdateEnhanced} 
                  userId={user.uid} 
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/knowledgeBase/:competenceId?" element={
              user ? (
                <KnowledgeBase 
                  userId={user.uid} 
                  competence={currentCompetence} 
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/chat/:competenceId" element={
              user ? (
                <AladinnContextualChat 
                  competence={currentCompetence} 
                  userId={user.uid}
                  onNavigate={handleNavigation} // Ajouter cette ligne
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/progression" element={
              user ? (
                <Dashboard 
                  userId={user.uid} 
                  activeTab="progression" 
                  onCompetenceSelect={handleSelectCompetenceEnhanced}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />
            
            <Route path="/competencesList" element={
              user ? (
                <ObjectifsList
                  userId={user.uid}
                  onSelectCompetence={handleSelectCompetenceEnhanced}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/actionPlan/:competenceId?" element={
              user ? (
                <ActionSpace 
                  competence={currentCompetence} 
                  onExerciseComplete={handleProgressUpdateEnhanced} 
                  userId={user.uid}
                  onCompetenceLoaded={handleCompetenceLoaded}
                  onPlanUpdate={handleActionPlanUpdate}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/intent-analysis/:competenceId" element={
              user ? (
                <IntentAnalyzerSpace 
                  propCompetenceId={currentCompetence?.id}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/share/:competenceId" element={
              user ? (
                <ShareCompetence 
                  userId={user?.uid}
                />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/jina/search" element={
              user ? (
                <JinaAISearch />
              ) : (
                <Navigate to="/" replace />
              )
            } />

            <Route path="/admin/*" element={
              <AdminAuthProvider>
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              </AdminAuthProvider>
            } />

            <Route path="/notifications/*" element={
              user ? <NotificationPage userId={user.uid} /> : <Navigate to="/" replace />
            } />

            <Route path="/farmroad" element={
              user ? <FarmRoadDashboard /> : <Navigate to="/" replace />
            } />

            <Route path="/farmroad/analysis" element={
              user ? <FarmRoadImageAnalysis /> : <Navigate to="/" replace />
            } />

          </Routes>
        </div>
      </main>

      {/* Système de notification personnalisé - RESPONSIVE avec RTL */}
      {snackbar.open && (
        <div className={`fixed bottom-4 ${isRTL ? 'left-2 lg:left-4' : 'right-2 lg:right-4'} z-50 flex items-center max-w-xs lg:max-w-sm`}
             style={{ bottom: isMobile ? '80px' : '16px' }}>
          <div 
            className={`flex items-center p-3 lg:p-4 rounded-lg shadow-lg ${
              snackbar.severity === 'success' ? `bg-green-50 text-green-800 ${isRTL ? 'border-r-4 border-green-500' : 'border-l-4 border-green-500'}` :
              snackbar.severity === 'error' ? `bg-red-50 text-red-800 ${isRTL ? 'border-r-4 border-red-500' : 'border-l-4 border-red-500'}` :
              snackbar.severity === 'warning' ? `bg-yellow-50 text-yellow-800 ${isRTL ? 'border-r-4 border-yellow-500' : 'border-l-4 border-yellow-500'}` :
              `bg-blue-50 text-blue-800 ${isRTL ? 'border-r-4 border-blue-500' : 'border-l-4 border-blue-500'}`
            } ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {snackbar.severity === 'success' ? (
              <CheckCircle className="h-4 lg:h-5 w-4 lg:w-5 mx-2 lg:mx-3 flex-shrink-0" />
            ) : snackbar.severity === 'error' ? (
              <AlertTriangle className="h-4 lg:h-5 w-4 lg:w-5 mx-2 lg:mx-3 flex-shrink-0" />
            ) : snackbar.severity === 'warning' ? (
              <AlertTriangle className="h-4 lg:h-5 w-4 lg:w-5 mx-2 lg:mx-3 flex-shrink-0" /> 
            ) : (
              <Info className="h-4 lg:h-5 w-4 lg:w-5 mx-2 lg:mx-3 flex-shrink-0" />
            )}
            <span className={`text-xs lg:text-sm mx-2 lg:mx-8 line-clamp-2 ${isRTL ? 'text-right' : 'text-left'}`}>{snackbar.message}</span>
            <button 
              onClick={handleSnackbarClose} 
              className="ml-auto text-gray-500 hover:text-gray-800 flex-shrink-0"
            >
              <X className="h-3 lg:h-4 w-3 lg:w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
